import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { ceil } from 'lodash';
import { AdGroup } from '@bingads-webui-campaign/campaign-ui-schemas';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { isMSStoreAdsCampaign } from '@bingads-webui-campaign-react/ms-store-ads-common';
import { CampaignType } from '@bingads-webui-campaign-react/shell-ui-common';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Form } from '@bingads-webui-react/primitive';
import { BiddingStrategyToRangeKey, BiddingStrategyToBidParam } from '@bingads-webui-campaign/audience-campaign-shared';
import { defaultMSStoreMinSuggestionBid } from '@bingads-webui-campaign-legacy/budget/shared/defaultBid';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { ValidationGroup, Input } from '@bingads-webui-react/primitive-validation';
import { isXandrCampaign, isXandrVideoAdsCampaign } from '@bingads-webui-campaign-react/constants';
import pageContext from '@bingads-webui-static-legacy/page-context';
import { getBidEditorStyle } from '../styles';
import { BaseEditor, KEYSENTER } from './base-editor';
import { getBulkEditResponseErrors } from '../helpers';

const adGroupSchema = {
  ...AdGroup,
  properties: {
    ...AdGroup.properties,
    SearchBid: {
      ...AdGroup.properties.SearchBid,
      nullable: false,
    },
    CpmBid: {
      ...AdGroup.properties.CpmBid,
      nullable: false,
    },
    CpvBid: {
      ...AdGroup.properties.CpvBid,
      nullable: false,
    },
  },
};

const topicMapping = {
  ManualCpc: 'pop_BA_AdGroup_SearchBid',
  ManualCpv: 'Page_CampaignGrids_CpvBid',
  ManualCpm: 'Page_CampaignGrids_CpmBid',
};

class AdGroupBidEditorInternal extends React.Component {
  static propTypes = {
    i18n: PropTypes.shape({
      getString: PropTypes.func,
    }).isRequired,
    data: PropTypes.shape({
      SearchBid: PropTypes.number,
      CpvBid: PropTypes.number,
      CpmBid: PropTypes.number,
    }).isRequired,
    classes: PropTypes.shape({}).isRequired,
    onSave: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    updateBid: PropTypes.func.isRequired,
    serverContext: PropTypes.shape({
      CurrentAccount: PropTypes.shape({}).isRequired,
    }).isRequired,
    campaignType: PropTypes.number.isRequired,
    bidStrategy: PropTypes.string.isRequired,
    campaignSubType: PropTypes.string.isRequired,
    permissions: PropTypes.shape({
      IsBidLessThan5CentsInShoppingCampaignEnabled: PropTypes.bool.isRequired,
    }).isRequired,
    summaryBarActivityLogger: PropTypes.shape({
      summaryBarBidActivity: PropTypes.any,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.activityLogger = props.summaryBarActivityLogger
      && props.summaryBarActivityLogger.summaryBarBidActivity.create();
    this.isMSStoreAdsCampaign = isMSStoreAdsCampaign(pageContext.CurrentCampaign);
    this.defaultMSStoreMinSuggestionBid = defaultMSStoreMinSuggestionBid();
  }

  state = {
    isValid: false,
    data: this.props.data,
    serverErrors: [],
    isLowerThanMinBidSuggestion: this.props.data.SearchBid < defaultMSStoreMinSuggestionBid(),
  };

  onValidate = (result) => {
    const bidType = BiddingStrategyToBidParam[this.props.bidStrategy] || 'SearchBid';
    const newBid = result.data[bidType];
    if (newBid !== this.state.data[bidType]) {
      this.setState({
        isValid: result.isValid,
        data: { [bidType]: newBid },
        isLowerThanMinBidSuggestion: newBid < this.defaultMSStoreMinSuggestionBid,
      });
    }
  };

  onSave = () => {
    if (this.state.isValid) {
      this.props.updateBid(this.state.data)
        .then((response) => {
          this.props.onSave(response);
          if (this.activityLogger) {
            this.activityLogger.trace({
              updateStatus: 'successful',
              value: this.state.data,
              component: 'ad group summary bar',
            });
          }
        })
        .catch((response) => {
          const serverErrors = getBulkEditResponseErrors(response, this.props.i18n, 'newadgroup');
          this.setState({ serverErrors });

          // server error is reported to user trace here as well
          if (this.activityLogger) {
            this.activityLogger.trace({
              updateStatus: 'failed',
              serverErrors,
              component: 'ad group summary bar',
            });
          }
        });
    }
  };

  onKeyDown = (e) => {
    if (e && e.which === KEYSENTER) {
      e.preventDefault();
      e.stopPropagation();
      this.onSave();
    }
  };

  updateBidSchemaRange = () => {
    const {
      serverContext, campaignType, permissions, bidStrategy, campaignSubType, dealMinimumBid,
    } = this.props;
    const useProductGroupBidRange = permissions.IsBidLessThan5CentsInShoppingCampaignEnabled && campaignType === CampaignType.Shopping;
    let bidRangeKey = useProductGroupBidRange ? 'ProductGroupBidRange' : (BiddingStrategyToRangeKey[bidStrategy] || 'KeywordBidRange');
    if (this.isMSStoreAdsCampaign) {
      bidRangeKey = 'WindowsStoreAdsBidRange';
    } else if (isXandrVideoAdsCampaign(campaignSubType)) {
      if (bidStrategy === 'ManualCpv') {
        bidRangeKey = 'VideoViewsCPVBidRange';
      } else if (bidStrategy === 'ManualCpm') {
        bidRangeKey = 'VideoViewsCPMBidRange';
      }
    }
    const bidRange = _.result(serverContext, bidRangeKey);
    let min = _.result(bidRange, 'FloorValue');
    const max = _.result(bidRange, 'CeilingValue');

    if (isXandrCampaign(campaignSubType)
        && permissions.IsDealEnabledForMsAds
        && dealMinimumBid && dealMinimumBid < max) {
      min = _.max([min, ceil(dealMinimumBid, 2)]);
    }

    const bidType = BiddingStrategyToBidParam[bidStrategy] || 'SearchBid';
    adGroupSchema.properties[bidType].minimum = min;
    adGroupSchema.properties[bidType].maximum = max;
  };

  errorProcessor = (errors) => {
    const { i18n } = this.props;
    const customErrors = {
      required: i18n.getString(_TL_('Bid is required.')),
    };

    return _.map(
      errors,
      (oldError, key) => customErrors[key] || oldError
    );
  };

  render() {
    const {
      i18n, classes, onCancel, serverContext, bidStrategy,
    } = this.props;
    const {
      data,
      serverErrors,
      isValid,
      isLowerThanMinBidSuggestion,
    } = this.state;
    const { CurrentAccount } = serverContext;
    const { CurrencyCode } = CurrentAccount;

    this.updateBidSchemaRange(adGroupSchema);

    return (
      <BaseEditor onSave={this.onSave} onCancel={onCancel} isValid={isValid} isLoading={false}>
        <ValidationGroup validationProps={{
          schema: adGroupSchema,
          onValidate: this.onValidate,
          data,
        }}
        >
          <Form className={classes.form}>
            <div className="search-bid-editor">
              <div>
                <span className="bid-label">{i18n.getString(_TL_('Default bid'))}</span>
                <HelpIcon
                  topic={topicMapping[bidStrategy] || 'SearchBid'}
                  ariaLabel={`${i18n.getString(_TL_('Click to learn more about bid'))}`}
                />
              </div>
              <div className="bid-input">
                <span id="adgroup-bid-currencycode" className="input-addon">{CurrencyCode}</span>
                <Input
                  aria-label={i18n.getString(_TL_('Search Bid'))}
                  className="adgroup-bid-edit-bid-input"
                  validationProps={{ field: BiddingStrategyToBidParam[bidStrategy] || 'SearchBid', errorProcessor: this.errorProcessor }}
                  onKeyDown={this.onKeyDown}
                />
              </div>
              { this.isMSStoreAdsCampaign && isLowerThanMinBidSuggestion &&
              <div role="alert" className="bidWarning">
                <div className="alert alert-info">
                  <div className="alert-image" />
                  <div className="alert-info-inline">
                    <ReactTemplate
                      model={{
                        link: text => <a target="_blank" rel="noreferrer" href="https://help.ads.microsoft.com/#apex/ads/en/60170/-1">{text}</a>,
                        minSuggestionBid: this.defaultMSStoreMinSuggestionBid
                      }}
                      template={i18n.getString(_TL_('We recommend raising your bid to over {{minSuggestionBid}} for optimal results, because a bid set any lower risks a lack of impressions. {{link}}Learn more{{/link}}'))}
                    />
                  </div>
                </div>
              </div>}
            </div>
            <ErrorMessages messages={_.pluck(serverErrors, 'message')} />
          </Form>
        </ValidationGroup>
      </BaseEditor>
    );
  }
}

const wrappers = [
  withServerContext,
  _.partial(withDefaultStyles, _, getBidEditorStyle),
];

const Wrapped = _.compose(...wrappers)(AdGroupBidEditorInternal);

export { Wrapped as AdGroupBidEditor };
